import Kendra from 'aws-sdk/clients/kendra';
import React from 'react';
import {
    DataSourceNameLookup,
    IndexFieldNameToDocumentAttributeValueType,
    selectHasReachedMaxFiltersForFacet,
} from './utils';
import { AvailableFacetRetriever } from './AvailableFacetRetriever';
import { SelectedFacetManager } from './SelectedFacetManager';
import _ from 'lodash';
import { DocumentAttributeKeys } from '../../constants';
import { DataSourceFacet } from './components/DataSourceFacet';
import { StringFacet } from './components/StringFacet';
import { facetConfiguration } from '../configuration';
import { useTranslate } from 'src/i18n/useTranslate';
import { ExpandableSection } from '@cloudscape-design/components';

interface Props {
    index?: Kendra.DescribeIndexResponse;
    attributeTypeLookup?: IndexFieldNameToDocumentAttributeValueType;
    availableFacets?: AvailableFacetRetriever;
    selectedFacets: SelectedFacetManager;
    open: boolean;

    onSelectedFacetsChanged: (
        updatedSelectedFacets: SelectedFacetManager
    ) => void;
    onExpand: () => void;

    // Required only if filtering by data source is desired
    dataSourceNameLookup?: DataSourceNameLookup;
}

export const Facets = ({
    attributeTypeLookup,
    availableFacets,
    dataSourceNameLookup,
    selectedFacets,
    open,
    onExpand,
    onSelectedFacetsChanged,
    index,
}: Props) => {
    const t = useTranslate();

    const onClickResetFilters = () => {
        onSelectedFacetsChanged(selectedFacets.clearAll());
    };

    const onStringSelectionChange = (
        key: Kendra.DocumentAttributeKey,
        value: Kendra.DocumentAttributeValue,
        isSelected: boolean
    ) => {
        onSelectedFacetsChanged(
            selectedFacets.setIsSelected(key, value, isSelected)
        );
    };

    const onClearStringSelections = (key: Kendra.DocumentAttributeKey) => {
        onSelectedFacetsChanged(selectedFacets.clearSelectionsOf(key));
    };

    if (!availableFacets || !attributeTypeLookup || !selectedFacets) {
        return null;
    }

    const attributeNames = availableFacets.getAvailableAttributeNames();

    if (_.isEmpty(attributeNames)) {
        if (!selectedFacets.isEmpty()) {
            // Handle the edge case where we have filtered down to no results
            return (
                <ExpandableSection
                    onChange={onClickResetFilters}
                    headerText={t('reset_filters', 'Reset Filters')}
                ></ExpandableSection>
            );
        } else {
            return null;
        }
    }

    return (
        <ExpandableSection onChange={onExpand} expanded={open}>
            <div data-testid="attributes-container">
                {attributeNames.map((attributeName) => {
                    const disableAdd = selectHasReachedMaxFiltersForFacet(
                        index,
                        selectedFacets.getAllSelected(),
                        attributeName
                    );

                    if (attributeName === DocumentAttributeKeys.DataSourceId) {
                        return (
                            <div data-testid="data-source-facet">
                                <DataSourceFacet
                                    key={attributeName}
                                    dataSourceNameLookup={dataSourceNameLookup}
                                    disableAdd={disableAdd}
                                    valueCountPairs={availableFacets.get(
                                        DocumentAttributeKeys.DataSourceId
                                    )}
                                    facetConfiguration={facetConfiguration}
                                    onSelectionChange={onStringSelectionChange}
                                    onClear={onClearStringSelections}
                                    selectedFacets={selectedFacets}
                                />
                            </div>
                        );
                    } else {
                        return (
                            <StringFacet
                                key={attributeName}
                                attributeName={attributeName}
                                valueCountPairs={availableFacets.get(
                                    attributeName
                                )}
                                disableAdd={disableAdd}
                                facetConfiguration={facetConfiguration}
                                onSelectionChange={onStringSelectionChange}
                                onClear={onClearStringSelections}
                                selectedFacets={selectedFacets}
                            />
                        );
                    }
                })}
            </div>
        </ExpandableSection>
    );
};
